/**
 * Shared HTML render template for the AI-generated notice summary.
 *
 * Used by:
 *   - `NoticeSummaryViewer/index.js` — the existing user-facing modal that
 *     re-fetches via POST /ai/generate-summary on every open.
 *   - `pages/admin/services/AdminNoticeSummaryView.jsx` — the admin
 *     Summary Details tab (Tab 3) used across GST Notice & Order, GST
 *     Additional Notices, IT EProceeding, and IT RoD detail pages.
 *     Displays the SAME render but pulls `summaryData` directly from
 *     the already-stored `notice_summary` column on each model (no AI
 *     regeneration, no buttons — pure inline view).
 *
 * Both call sites use `dangerouslySetInnerHTML={{ __html: ... }}` to
 * inject the returned string. Keep the visual structure stable across
 * future tweaks — both surfaces should change together.
 */

const formatCurrency = (amount) => {
  if (!amount || amount === 0) return "₹ 0";
  return `₹ ${amount.toLocaleString("en-IN")}`;
};

const isDeadlineNear = (deadline) => {
  if (!deadline || deadline === "Not specified") return false;
  try {
    const parts = deadline.split("/");
    if (parts.length !== 3) return false;
    const deadlineDate = new Date(parts[2], parts[1] - 1, parts[0]);
    const today = new Date();
    const diffDays = Math.floor(
      (deadlineDate - today) / (1000 * 60 * 60 * 24),
    );
    return diffDays <= 7 && diffDays >= 0;
  } catch (e) {
    return false;
  }
};

export const buildNoticeSummaryHTML = (summaryData) => {
  if (!summaryData) return "";

  const breakdown = summaryData.monetary_demands?.breakdown || [];
  const totalTax = breakdown.reduce((sum, item) => sum + (item.tax || 0), 0);
  const totalInterest = breakdown.reduce(
    (sum, item) => sum + (item.interest || 0),
    0,
  );
  const totalPenalty = breakdown.reduce(
    (sum, item) => sum + (item.penalty || 0),
    0,
  );
  const grandTotal =
    summaryData.monetary_demands?.total ||
    totalTax + totalInterest + totalPenalty;

  return `
      <div style="font-family: Arial, sans-serif; color: #333; padding: 20px;">
        ${
          summaryData.notice_language &&
          summaryData.notice_language !== "English"
            ? `
          <div style="padding: 10px; margin-bottom: 15px; background: #e8f4f8; border-left: 4px solid #3498db; border-radius: 4px;">
            <strong style="color: #2c3e50;">📝 Notice Language:</strong> <span style="color: #3498db;">${
              summaryData.notice_language
            }</span>
            ${
              summaryData.notice_language === "Hindi"
                ? '<span style="margin-left: 10px; font-size: 0.9em; color: #7f8c8d;">(Summary translated to English)</span>'
                : ""
            }
          </div>
        `
            : ""
        }

        <h2 style="color: #2c3e50; font-weight: bold; border-bottom: 3px solid #3498db; padding-bottom: 10px; margin-top: 0; margin-bottom: 15px; font-size: 2em;">Notice Summary</h2>

        <h2 style="color: #34495e; font-weight: bold; margin-top: 30px; border-bottom: 2px solid #bdc3c7; padding-bottom: 8px; margin-bottom: 15px; font-size: 1.4em;">Overview</h2>
        <div style="margin: 20px 0;">
          <p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Notice Type:</span> <span style="margin-left: 10px;">${
            summaryData.notice_type || "Not specified"
          }</span></p>
          <p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Section:</span> <span style="margin-left: 10px;">${
            summaryData.section || "Not specified"
          }</span></p>
          ${
            summaryData["Assessment Year"]
              ? `<p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Assessment Year:</span> <span style="margin-left: 10px;">${summaryData["Assessment Year"]}</span></p>`
              : ""
          }
          <p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Notice Reference:</span> <span style="margin-left: 10px;">${
            summaryData.notice_reference || "Not mentioned"
          }</span></p>
          <p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Issuing Authority:</span> <span style="margin-left: 10px;">${
            summaryData.issuing_authority || "Not mentioned"
          }</span></p>
          <p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Response Deadline:</span> <span style="margin-left: 10px; color: ${
            isDeadlineNear(summaryData.response_deadline)
              ? "#e74c3c"
              : "#27ae60"
          }; font-weight: bold;">${
    summaryData.response_deadline || "Not specified"
  }</span></p>
        </div>

        <h2 style="color: #34495e; font-weight: bold; margin-top: 30px; border-bottom: 2px solid #bdc3c7; padding-bottom: 8px; margin-bottom: 15px; font-size: 1.4em;">Key Concerns</h2>
        <div style="margin: 20px 0;">
          <ol style="margin: 10px 0; padding-left: 30px;">
            ${
              summaryData.key_concerns && summaryData.key_concerns.length > 0
                ? summaryData.key_concerns
                    .map(
                      (c) =>
                        `<li style="margin: 8px 0; line-height: 1.6;">${c}</li>`,
                    )
                    .join("")
                : "<li>No specific concerns listed</li>"
            }
          </ol>
        </div>

        <h2 style="color: #34495e; font-weight: bold; margin-top: 30px; border-bottom: 2px solid #bdc3c7; padding-bottom: 8px; margin-bottom: 15px; font-size: 1.4em;">Financial Impact</h2>
        <div style="margin: 20px 0;">
          ${
            breakdown.length > 0
              ? `
            <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
              <thead>
                <tr style="background-color: #34495e; color: white;">
                  <th style="border: 1px solid #bdc3c7; padding: 12px; text-align: left; font-weight: bold;">Description</th>
                  <th style="border: 1px solid #bdc3c7; padding: 12px; text-align: right; font-weight: bold;">Tax</th>
                  <th style="border: 1px solid #bdc3c7; padding: 12px; text-align: right; font-weight: bold;">Interest</th>
                  <th style="border: 1px solid #bdc3c7; padding: 12px; text-align: right; font-weight: bold;">Penalty</th>
                  <th style="border: 1px solid #bdc3c7; padding: 12px; text-align: right; font-weight: bold;">Total</th>
                </tr>
              </thead>
              <tbody>
                ${breakdown
                  .map(
                    (item) => `
                  <tr>
                    <td style="border: 1px solid #bdc3c7; padding: 10px;">${
                      item.description
                    }</td>
                    <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace;">
                      ${formatCurrency(item.tax || 0)}
                    </td>
                    <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace;">
                      ${formatCurrency(item.interest || 0)}
                    </td>
                    <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace;">
                      ${formatCurrency(item.penalty || 0)}
                    </td>
                    <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace;">
                      ${formatCurrency(item.amount || 0)}
                    </td>
                  </tr>
                `,
                  )
                  .join("")}
                <tr style="background-color: #ecf0f1; font-weight: bold;">
                  <td style="border: 1px solid #bdc3c7; padding: 10px; font-weight: bold;">TOTAL</td>
                  <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace; font-weight: bold;">
                    ${formatCurrency(totalTax)}
                  </td>
                  <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace; font-weight: bold;">
                    ${formatCurrency(totalInterest)}
                  </td>
                  <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace; font-weight: bold;">
                    ${formatCurrency(totalPenalty)}
                  </td>
                  <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace; font-weight: bold;">
                    ${formatCurrency(totalTax + totalInterest + totalPenalty)}
                  </td>
                </tr>
                <tr style="background: #d5dbdb; font-weight: bold;">
                  <td colspan="4" style="border: 1px solid #bdc3c7; padding: 10px; font-weight: bold;">GRAND TOTAL</td>
                  <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace; color: #e74c3c; font-size: 1.1em; font-weight: bold;">
                    ${formatCurrency(grandTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          `
              : '<p style="color: #95a5a6; font-style: italic;">No monetary demand specified in this notice.</p>'
          }
        </div>

        <h2 style="color: #34495e; font-weight: bold; margin-top: 30px; border-bottom: 2px solid #bdc3c7; padding-bottom: 8px; margin-bottom: 15px; font-size: 1.4em;">Recommended Actions</h2>
        <div style="margin: 20px 0;">
          <ol style="margin: 10px 0; padding-left: 30px;">
            ${
              summaryData.recommended_actions &&
              summaryData.recommended_actions.length > 0
                ? summaryData.recommended_actions
                    .map(
                      (a) =>
                        `<li style="margin: 8px 0; line-height: 1.6;">${a}</li>`,
                    )
                    .join("")
                : "<li>No specific actions recommended</li>"
            }
          </ol>
        </div>
      </div>
    `;
};
